import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  getContractSignDetailsAction,
  setUpdateContractSignResponse,
  updateContractSignAction,
} from "../store/reducers/contractStorageSlice";

const BRAND = "#0f5c55";
const BRAND_SOFT = "#effaf7";
const BRAND_BORDER = "#9fd8cc";
const BORDER = "#dfe7ec";
const MUTED = "#6b7a85";

// ---- Replace with real data from your API ----
const booking = {
  company: "Trunk Logistics Limited",
  address: "57 Southend Road, Grays, Essex, RM17 5NL",
  companyReg: "11964263",
  vatReg: "321334840",
  ref: "BVDBK-HOHNW",
  customerName: "Susie Sheppard",
  customerCompany: "Crayford Freight Services Ltd",
  signerEmail: "susie86@hotmail.co.uk",
  units: ["SSR-22", "SSR-23", "SSR-24", "SSR-25", "SSR-26"],
  feePercent: 5,
  contractPdfUrl: "#",
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: "#8a98a3",
  textTransform: "uppercase" as const,
  mb: 1,
};

const fieldLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  color: "#3d4a54",
  textTransform: "uppercase" as const,
  mb: 0.75,
};

const InfoBox = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      flex: 1,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: "#f8fafb",
      px: 1.75,
      py: 1.25,
    }}
  >
    <Typography sx={{ fontSize: 11, color: MUTED, mb: 0.25 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const InsuranceOption = ({
  selected,
  title,
  description,
  onClick,
  icon,
   disabled = false,
}: {
  selected: boolean;
  title: string;
  description: string;
  onClick: () => void;
  icon?: string;
  disabled?: boolean;
}) => (
  <Box
    onClick={onClick}
    role="radio"
    aria-checked={selected}
    tabIndex={0}
   onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      flex: 1,
     cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      pointerEvents: disabled ? "none" : "auto",
      display: "flex",
      gap: 1.25,
      p: 1.75,
      borderRadius: 2,
      border: `1.5px solid ${selected ? BRAND : BORDER}`,
      background: selected ? BRAND_SOFT : "#fff",
      transition: "all .15s ease",
      "&:hover": { borderColor: selected ? BRAND : "#b9c6cf" },
      "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
    }}
  >
    <Box
      sx={{
        mt: "2px",
        width: 16,
        height: 16,
        flexShrink: 0,
        borderRadius: "50%",
        border: `1.5px solid ${selected ? "#1976d2" : "#9aa7b0"}`,
        display: "grid",
        placeItems: "center",
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#1976d2",
          }}
        />
      )}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1d2a33" }}>
        {icon && <span style={{ marginRight: 4 }}>{icon}</span>}
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: 11.5, color: MUTED, mt: 0.25, lineHeight: 1.4 }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const ContractSignPage = () => {
  const [contentValue, setContentValue] = useState("5000.00");
  const [agreed, setAgreed] = useState(false);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { contractSignDetails, isContractSignUpdated } = useSelector(
    (state: any) => state.contractStorage,
  );
  const [insurance, setInsurance] = useState<boolean | null>(null);
  const [signature, setSignature] = useState(
    contractSignDetails.customerSign ?? "",
  );
  const [customerDetail, setCustomerDetail] = useState(
    contractSignDetails?.customer,
  );
  const tenantId = searchParams.get("tenantId");
  const contractId = searchParams.get("contractId");
  const token = searchParams.get("token");

  console.log("tenantId:", tenantId);
  console.log("contractId:", contractId);
  console.log("token:", token);

  useEffect(() => {
    if (tenantId && contractId && token) {
      dispatch(
        getContractSignDetailsAction({
          tenantId: tenantId,
          contractId: contractId,
          token: token,
        }),
      );
    }
  }, [dispatch, tenantId, contractId, token]);

  useEffect(() => {
    if (isContractSignUpdated) {
      dispatch(
        getContractSignDetailsAction({
          tenantId: tenantId,
          contractId: contractId,
          token: token,
        }),
      );
      dispatch(setUpdateContractSignResponse(false));
    }
  }, [dispatch, isContractSignUpdated,tenantId, contractId, token]);

  useEffect(() => {
    if (contractSignDetails) {
      setSignature(contractSignDetails.customerSign ?? "");
      setInsurance(contractSignDetails.isInsuranceAccepted ?? null);
      setCustomerDetail(contractSignDetails.customer ?? null);
    }
  }, [contractSignDetails]);

  const canSubmit = agreed && signature.trim().length > 1 && insurance !== null;

  console.log({
    agreed,
    signature,
    insurance,
    isSigned: contractSignDetails?.isSigned,
    canSubmit,
  });
  const handleSign = () => {
    if (!canSubmit) return;

    const payload = {
      tenantId,
      contractId,
      token,
      customerSign: signature.trim(),
      isInsuranceAccepted: insurance,
    };

    console.log("Sign Contract Payload:", payload);
    dispatch(updateContractSignAction(payload));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f5f7fa 0%, #edf4f7 100%)",
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            background: "#fff",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "#f8fafb",
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="flex-start">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: BRAND,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  fontSize: 24,
                }}
              >
                <img
                  src={contractSignDetails?.companyLogo}
                  alt="logo"
                  style={{ height: "50px", width: "50px" }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 18, fontWeight: 800, color: "#12202a" }}
                >
                  {contractSignDetails.companyName}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: MUTED }}>
                  {contractSignDetails.companyAddress}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#c28a55" }}>
                  Post Code: {contractSignDetails.companyPostCode}
                </Typography>
              </Box>
              <Box
                sx={{
                  border: `1px solid ${BORDER}`,
                  background: "#fff",
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                  fontFamily: "monospace",
                  fontSize: 10.5,
                  color: "#4b5a65",
                  whiteSpace: "nowrap",
                }}
              >
                {contractSignDetails?.status}
              </Box>
            </Stack>
          </Box>

          {/* contractSignDetails */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Typography sx={sectionLabelSx}>
              Customer &amp; Storage Booking
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <InfoBox label="Customer Name">
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: "#12202a" }}
                >
                  {customerDetail?.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#4b5a65" }}>
                  {contractSignDetails.customer?.email}
                </Typography>
              </InfoBox>
              <InfoBox label="Customer Email">
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: 600, color: "#12202a" }}
                >
                  {customerDetail?.email}
                </Typography>
                {/* <Typography sx={{ fontSize: 11.5, color: "#1f9d6b", mt: 0.25 }}>
                  ● Identity verified
                </Typography> */}
              </InfoBox>
            </Stack>

            <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 2, mb: 0.75 }}>
              Allocated Unit Reference Numbers (
              {contractSignDetails?.units?.length} Units):
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {contractSignDetails?.units?.map((u: any, index: number) => (
                <Chip
                  key={index}
                  size="small"
                  label={u?.unitTypeName}
                  icon={<span style={{ fontSize: 12, marginLeft: 6 }}>📍</span>}
                  sx={{
                    borderRadius: 1,
                    background: BRAND_SOFT,
                    border: `1px solid ${BRAND_BORDER}`,
                    color: BRAND,
                    fontWeight: 700,
                    fontFamily: "monospace",
                    fontSize: 11,
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box sx={{ borderTop: `1px solid ${BORDER}` }} />

          {/* Insurance */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Typography sx={fieldLabelSx}>Insurance Protection *</Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              role="radiogroup"
              
            >
              <InsuranceOption
                selected={insurance === true}
                onClick={() => setInsurance(true)}
                disabled={!!contractSignDetails?.isSigned}
                icon="🛡️"
                title="Accept Insurance"
                description="Enhanced liability protection up to declared replacement value."
              />
              <InsuranceOption
                selected={insurance === false}
                onClick={() => setInsurance(false)}
                disabled={!!contractSignDetails?.isSigned}
                title="Decline Insurance"
                description={`You arrange own insurance; facility liability capped at ${contractSignDetails?.insurancePercentage}%.`}
              />
            </Stack>
          </Box>

          <Box sx={{ borderTop: `1px solid ${BORDER}`, mx: 3 }} />

          {/* Signature */}
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Typography sx={fieldLabelSx}>
              Customer Signature (Type Name) *
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              disabled={!!contractSignDetails?.isSigned}
              InputProps={{ sx: { borderRadius: 2, background: "#f8fafb" } }}
            />
          </Box>

          <Box sx={{ borderTop: `1px solid ${BORDER}`, mx: 3 }} />

          {/* Agreement */}
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: `1px solid ${BRAND_BORDER}`,
                background: BRAND_SOFT,
                borderRadius: 2,
                px: 1,
                py: 0.75,
              }}
            >
              <Checkbox
                size="small"
                checked={agreed}
                disabled={!!contractSignDetails?.isSigned}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{ color: "#8a98a3", "&.Mui-checked": { color: BRAND } }}
              />
              <Typography
                sx={{ fontSize: 12, color: "#2b3a44", lineHeight: 1.4 }}
              >
                I confirm that I have read and agree to the terms, conditions,
                and clauses outlined in the{" "}
              </Typography>
            </Box>
          </Box>

          {/* CTA */}
          <Box sx={{ px: 3, pt: 1.5, pb: 3 }}>
            <Button
              fullWidth
              disabled={!canSubmit || !!contractSignDetails?.isSigned}
              onClick={handleSign}
              startIcon={<span>📝</span>}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                color: "#fff",
                background: BRAND,
                "&:hover": { background: "#0b4a44" },
                "&.Mui-disabled": { background: "#a9c4c0", color: "#fff" },
              }}
            >
              Sign &amp; Complete Agreement
            </Button>
            {/* <Typography
              sx={{ fontSize: 11, color: MUTED, textAlign: "center", mt: 1.25 }}
            >
              Upon signing, your fully executed PDF contract with embedded
              certificate will be downloaded.
            </Typography> */}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ContractSignPage;
